import React, { useState } from 'react';
import { motion } from 'framer-motion';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';

const OpdrachtPlaatsen = () => {
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    location: '',
    budget: '',
    deadline: '',
    urgency: 'normal'
  });

  const categories = [
    { value: 'electrical', label: 'Elektriciteit' },
    { value: 'plumbing', label: 'Loodgieterij' },
    { value: 'hvac', label: 'Verwarming & Koeling' },
    { value: 'maintenance', label: 'Onderhoud' },
    { value: 'cleaning', label: 'Reinigingswerkzaamheden' },
    { value: 'other', label: 'Overig' }
  ];

  const urgencyLevels = [
    { value: 'low', label: 'Laag', color: 'text-green-600' },
    { value: 'normal', label: 'Normaal', color: 'text-blue-600' },
    { value: 'high', label: 'Hoog', color: 'text-orange-600' },
    { value: 'urgent', label: 'Urgent', color: 'text-red-600' }
  ];

  const workTypeOptions = [
    { value: 'solar', label: 'Zonnepanelen' },
    { value: 'heatpump', label: 'Warmtepompen' },
    { value: 'battery', label: 'Thuisbatterijen' },
    { value: 'maintenance', label: 'Technisch onderhoud' },
    { value: 'ventilation', label: 'Ventilatie systemen' },
    { value: 'other', label: 'Overig' }
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    console.log('Opdracht plaatsen:', formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  return (
    <MainLayout>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-4xl mx-auto"
      >
        <motion.div variants={itemVariants} className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="PlusCircle" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground">
                Personeel Aanvragen
              </h1>
              <p className="text-muted-foreground mt-1">
                Plaats een personeelsvraag en ontvang reacties van gekwalificeerde vakmensen
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-card rounded-xl border border-border p-6 lg:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <motion.div variants={itemVariants}>
              <label className="block text-sm font-medium text-foreground mb-2">
                Personeelsvraag Titel *
              </label>
              <Input
                type="text"
                placeholder="Bijv. Vakmensen gezocht voor zonnepanelenproject"
                value={formData?.title}
                onChange={(e) => handleChange('title', e?.target?.value)}
                required
              />
            </motion.div>

            <motion.div variants={itemVariants}>
              <label className="block text-sm font-medium text-foreground mb-2">
                Categorie *
              </label>
              <Select
                value={formData?.category}
                onChange={(e) => handleChange('category', e?.target?.value)}
                required
              >
                <option value="">Selecteer een categorie</option>
                {categories?.map(cat => (
                  <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                ))}
              </Select>
            </motion.div>

            <motion.div variants={itemVariants}>
              <label className="block text-sm font-medium text-foreground mb-2">
                Beschrijving *
              </label>
              <textarea
                className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground min-h-[120px]"
                placeholder="Geef een gedetailleerde beschrijving van de benodigde vakmensen en het project..."
                value={formData?.description}
                onChange={(e) => handleChange('description', e?.target?.value)}
                required
              />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div variants={itemVariants}>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Locatie *
                </label>
                <Input
                  type="text"
                  placeholder="Stad of postcode"
                  value={formData?.location}
                  onChange={(e) => handleChange('location', e?.target?.value)}
                  required
                />
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Budget (€)
                </label>
                <Input
                  type="number"
                  placeholder="Geschat budget"
                  value={formData?.budget}
                  onChange={(e) => handleChange('budget', e?.target?.value)}
                />
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div variants={itemVariants}>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Deadline
                </label>
                <Input
                  type="date"
                  value={formData?.deadline}
                  onChange={(e) => handleChange('deadline', e?.target?.value)}
                />
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Urgentie *
                </label>
                <Select
                  value={formData?.urgency}
                  onChange={(e) => handleChange('urgency', e?.target?.value)}
                  required
                >
                  {urgencyLevels?.map(level => (
                    <option key={level?.value} value={level?.value}>{level?.label}</option>
                  ))}
                </Select>
              </motion.div>
            </div>

            <motion.div 
              variants={itemVariants}
              className="flex gap-4 pt-4"
            >
              <Button
                type="submit"
                className="flex-1 md:flex-none"
              >
                <Icon name="Send" size={18} className="mr-2" />
                Personeelsvraag Plaatsen
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setFormData({
                  title: '',
                  category: '',
                  description: '',
                  location: '',
                  budget: '',
                  deadline: '',
                  urgency: 'normal'
                })}
              >
                Reset
              </Button>
            </motion.div>
          </form>
        </motion.div>

        <motion.div variants={itemVariants} className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: 'Users', title: 'Gekwalificeerde Vakmensen', desc: 'Alleen geverifieerde professionals' },
            { icon: 'Clock', title: 'Snelle Reacties', desc: 'Ontvang reacties binnen 24 uur' },
            { icon: 'Shield', title: 'Veilig & Betrouwbaar', desc: 'Beveiligde contracten en garantie' }
          ]?.map((feature, idx) => (
            <motion.div
              key={idx}
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              className="bg-card rounded-lg border border-border p-4 text-center"
            >
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Icon name={feature?.icon} size={20} color="var(--color-primary)" />
              </div>
              <h3 className="font-semibold text-foreground mb-1">{feature?.title}</h3>
              <p className="text-sm text-muted-foreground">{feature?.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </MainLayout>
  );
};

export default OpdrachtPlaatsen;